import React, { useState, useRef, useEffect } from 'react';
import I18n from 'utils/I18n';
import PropTypes from 'prop-types';
import Button from '@strava/ui/Button';
import createNetworkingClient from 'utils/networking-client';
import * as EmailValidator from 'email-validator';
import ReCAPTCHA from 'react-google-recaptcha';

import styles from './index.scss';
import SignupAgeGateModal from '../../../../components/shared/SignupAgeGateModal';
import DisclaimerText from '../../../../components/shared/DisclaimerText';
import {
  FacebookButton,
  GoogleButton,
  SIGN_UP,
  FACEBOOK,
  GOOGLE,
  EMAIL,
  elemAttrForAnalytics
} from '../../../../components/shared/AccountAuthButton';
import Flash from '../Flash';
import {
  REGISTRATION_PAGE,
  trackV2Onboarding
} from '../../../../utils/onboarding';

const networkingClient = createNetworkingClient();
const I18nPrefix = 'strava.registration';

const RegistrationForm = ({
  flash,
  authorizationUrls,
  locale,
  reCaptchaSiteKey,
  formAuthToken
}) => {
  const [openAgeGateModal, setOpenAgeGateModal] = useState(false);
  const [signupOption, setSignupOption] = useState(null);
  const [flashMessages, setFlashMessages] = useState(flash);

  const [formInput, setFormInput] = useState({});
  const [passwordErrorMsg, setPasswordErrMsg] = useState(null);
  const [emailErrorMsg, setEmailErrorMsg] = useState(null);

  const emailInput = useRef(null);
  const passwordInput = useRef(null);

  const recaptchaRef = useRef();

  useEffect(() => {
    trackV2Onboarding({
      page: 'register',
      action: 'screen_enter'
    });
  }, []);

  const useRecaptcha = reCaptchaSiteKey && reCaptchaSiteKey.length > 0;

  // eslint-disable-next-line no-unused-vars
  const logError = (error) => {
    // TODO [important] log error in sentry (monorepo). We should log errors that are blocking athletes from
    // creating account.
    setFlashMessages({ error: I18n.t(`${I18nPrefix}.error_message`) });
  };

  /**
   * This page requires for any API calls to be performed synchronously
   * since we want to block the flow and display user friendly error messages
   * when something goes wrong.
   *
   * We also want to only trigger the age-gating process when all pre-registration requests are
   * successful.
   *
   * @param {Object} config api call config - https://axios-http.com/docs/req_config
   * @returns {Object} result payload from endpoint
   */
  async function makeApiCall({ method, url, params, data }) {
    let result = null;
    try {
      const response = await networkingClient.request({
        method,
        url,
        params,
        data
      });
      if (response && response.status === 200) {
        result = response;
      } else {
        logError(response.error);
      }
    } catch (error) {
      logError(error);
    }
    return result;
  }

  /**
   *  Makes an api call to check if an email supplied by the athlete is already registered to an account
   * on strava.
   *
   * @returns {Object} response Payload from endpoint
   * @returns {boolean} response.data false if account is already registered (not unique)
   */
  async function isAlreadyRegisteredEmail(inputEmail) {
    const response = await makeApiCall({
      params: { email: inputEmail },
      url: '/athletes/email_unique',
      method: 'GET'
    });
    return response;
  }

  /**
   * Facebook, Google and Apple signup buttons onClick handler
   */
  const handleExtAuthBtnClick = (option) => {
    trackV2Onboarding({
      page: 'register',
      action: 'click',
      element: elemAttrForAnalytics(option)
    });
    setOpenAgeGateModal(true);
    setSignupOption(option);
  };

  /**
   * Actions to be performed when an athletes successfully submits their age on the age gate modal.
   */
  const handleAgeGateSubmit = async () => {
    setOpenAgeGateModal(false);

    const recaptchaToken = useRecaptcha
      ? await recaptchaRef.current.executeAsync()
      : '';

    if (signupOption === EMAIL) {
      const { email, password } = formInput;
      const response = await makeApiCall({
        data: {
          v2: true,
          email,
          password,
          'g-recaptcha-response': recaptchaToken,
          form_authenticity_token: formAuthToken
        },
        url: authorizationUrls[EMAIL],
        method: 'POST'
      });
      if (response) {
        // navigate to onboarding flow if no errors were encountered
        // flash message is populated and shown if an error occurred
        window.location = response.data.load;
      }
    } else {
      window.location = authorizationUrls[signupOption];
    }
  };

  /**
   * Validate athlete email -
   * 1 - value should be provided (required)
   * 2 - valid email address should be entered (eg. should have @)
   * 3 - email address should not be already registered on strava
   *
   * @returns {boolean} result true when the email is valid
   */
  async function validateEmail() {
    const { email } = formInput;
    if (!emailErrorMsg) {
      // avoid validating email if there is already an error that is not addressed by the athlete yet
      // this prevents making an unnecessary api call to check if email is already registered (see #isAlreadyRegisteredEmail)

      let isValidEmail = true;
      let emailValidationMsg = null;

      if (!(email && email.length > 0)) {
        emailValidationMsg = I18n.t(`${I18nPrefix}.email_required`);
        isValidEmail = false;
      } else if (!EmailValidator.validate(email)) {
        emailValidationMsg = I18n.t(`${I18nPrefix}.email_invalid`);
        isValidEmail = false;
      } else {
        const response = await isAlreadyRegisteredEmail(email);
        if (response) {
          if (response.data === false) {
            // false (already registered) or true (unique)
            emailValidationMsg = I18n.t(`${I18nPrefix}.email_taken`);
            isValidEmail = false;
          }
        } else {
          // api failure is logged in flash message
          isValidEmail = false;
        }
      }
      setEmailErrorMsg(emailValidationMsg);
      return isValidEmail;
    }
    return false;
  }

  /**
   * Validates athlete password
   * - value should be provided (required)
   * - max length of 40 chars
   * - min length of 8 chars
   *
   * @returns {boolean} result true when the password is valid
   */
  const validatePassword = () => {
    const { password } = formInput;

    if (!passwordErrorMsg) {
      // avoid validating email if there is already an error that is not addressed by the athlete yet
      let isValidPassword = true;
      let passwordValidationMsg = null;
      if (!(password && password.length > 0)) {
        passwordValidationMsg = I18n.t(`${I18nPrefix}.password_required`);
        isValidPassword = false;
      } else if (password.length > 40) {
        passwordValidationMsg = I18n.t(`${I18nPrefix}.password_max_length`);
        isValidPassword = false;
      } else if (password.length < 8) {
        passwordValidationMsg = I18n.t(`${I18nPrefix}.password_min_length`);
        isValidPassword = false;
      }
      setPasswordErrMsg(passwordValidationMsg);
      return isValidPassword;
    }
    return false;
  };

  const validateForm = async () => {
    return {
      isValidEmail: await validateEmail(),
      isValidPassword: validatePassword()
    };
  };

  /**
   *  Actions to perform when a user hits the "sign up" button (account registration using email).
   *  - validate user input (email and password)
   *  - when all validations pass, open age-gate modal
   */
  async function handleFormSubmission(event) {
    event.preventDefault();

    const { isValidEmail, isValidPassword } = await validateForm();

    trackV2Onboarding({
      page: 'register',
      action: 'click',
      element: 'signup',
      properties: {
        is_valid_email: isValidEmail,
        is_valid_password: isValidPassword
      }
    });

    if (isValidEmail && isValidPassword) {
      // no api call errors. email and password is valid - open age gate modal
      setOpenAgeGateModal(true);
      setSignupOption(EMAIL);
    } else {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      if (!isValidPassword) {
        passwordInput.current.focus();
      }
      if (!isValidEmail) {
        emailInput.current.focus(); // prioritize focus on email input if both password and email inputs are invalid
      }
    }
  }

  const handleInputChange = (event) => {
    const { target } = event;
    const { name } = target;

    setFormInput((prev) => {
      return { ...prev, [name]: target.value };
    });

    // clear input specific errors
    if (name === 'email' && emailErrorMsg) {
      setEmailErrorMsg(null);
    } else if (passwordErrorMsg) {
      setPasswordErrMsg(null);
    }
    // clear flash messages on all input changes
    if (flashMessages) {
      setFlashMessages(null);
    }
  };

  return (
    /**
     * [11/30/21] This form and its subcomponent are currently re-using orion styles which is against our goal
     * when reactifying components. Given that this page is scheduled for a potential re-design and
     * the limited amount of time allocated for registration age-gating implementation, we did not think making moves to build react components to extract
     * the styles from https://github.com/strava/orion/blob/069f890ff0326b71f9343efc9bf9d60b11c7c524/app/assets/stylesheets/orion-components/forms.css
     * is a priority at this point since this page is not being targeted for a move to the monorepo just yet.
     * * */
    <form
      className="order signup"
      onSubmit={handleFormSubmission}
      noValidate={true}
    >
      <Flash messages={flashMessages} />

      <FacebookButton
        type={SIGN_UP}
        onClick={() => handleExtAuthBtnClick(FACEBOOK)}
      />

      <GoogleButton
        type={SIGN_UP}
        onClick={() => handleExtAuthBtnClick(GOOGLE)}
      />

      <div className={`${styles.emailCaption} text-caption1 mt-md mb-md`}>
        {I18n.t(`${I18nPrefix}.sign_up_with_email`)}
      </div>

      <fieldset className="user-inputs mt-0 mb-0">
        <div className="form-group">
          <label className="vh" htmlFor="email">
            {I18n.t(`${I18nPrefix}.email`)}
          </label>
          <input
            className="form-control"
            type="email"
            name="email"
            id="email"
            ref={emailInput}
            placeholder={I18n.t(`${I18nPrefix}.email`)}
            onChange={handleInputChange}
            onBlur={validateEmail}
          />
          {emailErrorMsg && (
            <div
              data-testid="email-error-message"
              className={styles.errorMessage}
            >
              {emailErrorMsg}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="vh" htmlFor="password">
            {I18n.t(`${I18nPrefix}.password`)}
          </label>
          <input
            className="form-control"
            type="password"
            name="password"
            id="password"
            ref={passwordInput}
            placeholder={I18n.t(`${I18nPrefix}.password`)}
            onChange={handleInputChange}
            onBlur={validatePassword}
          />
          {passwordErrorMsg && (
            <div
              data-testid="password-error-message"
              className={styles.errorMessage}
            >
              {passwordErrorMsg}
            </div>
          )}
        </div>

        {useRecaptcha && (
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={reCaptchaSiteKey}
            size="invisible"
          />
        )}

        <Button type="submit" variant="primary" id="signup-button">
          {I18n.t(`${I18nPrefix}.sign_up`)}
        </Button>
      </fieldset>

      <DisclaimerText />

      <SignupAgeGateModal
        open={openAgeGateModal}
        onDismissCallback={() => {
          setOpenAgeGateModal(false);
        }}
        locale={locale}
        onSuccessCallback={handleAgeGateSubmit}
        page={REGISTRATION_PAGE}
      />
    </form>
  );
};

RegistrationForm.defaultProps = {
  flash: null
};

RegistrationForm.propTypes = {
  reCaptchaSiteKey: PropTypes.string.isRequired,
  formAuthToken: PropTypes.string.isRequired,
  flash: PropTypes.shape({}),
  authorizationUrls: PropTypes.shape({
    google: PropTypes.string.isRequired,
    facebook: PropTypes.string.isRequired
  }).isRequired,
  locale: PropTypes.string.isRequired
};

export default RegistrationForm;
